import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdCustomersId,
  deleteAdminV1StoresStoreIdCustomersIdAddressesAddressId,
  getAdminV1StoresStoreIdCustomers,
  getAdminV1StoresStoreIdCustomersId,
  getAdminV1StoresStoreIdCustomersIdAddresses,
  getAdminV1StoresStoreIdCustomersIdTimeline,
  patchAdminV1StoresStoreIdCustomersId,
  patchAdminV1StoresStoreIdCustomersIdAddressesAddressId,
  postAdminV1StoresStoreIdCustomers,
  postAdminV1StoresStoreIdCustomersIdAddresses,
  postAdminV1StoresStoreIdCustomersIdInvite,
  postAdminV1StoresStoreIdCustomersIdMerge,
  postAdminV1StoresStoreIdCustomersIdPasswordResetEmail,
  postAdminV1StoresStoreIdCustomersIdPrivacyRequests,
  type Customer,
  type CustomerAddress,
  type CustomerEmailSent,
  type GetAdminV1StoresStoreIdCustomers200,
  type GetAdminV1StoresStoreIdCustomersIdTimeline200,
  type GetAdminV1StoresStoreIdCustomersIdTimelineParams,
  type PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody,
  type PatchAdminV1StoresStoreIdCustomersIdBody,
  type PostAdminV1StoresStoreIdCustomersBody,
  type PostAdminV1StoresStoreIdCustomersIdAddressesBody,
  type PostAdminV1StoresStoreIdCustomersIdInviteBody,
  type PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsBodyKind,
  type PrivacyRequest,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=guest,active`, `totalSpent_gte=5000`), `cursor`, `limit`.
 */
export type CustomerListParams = ListParams;

/** The two set-password emails: an invitation (24 h link) or a password reset (15 min link). */
export type AccountEmailKind = 'invite' | 'password_reset';

export type CustomerPrivacyKind = PostAdminV1StoresStoreIdCustomersIdPrivacyRequestsBodyKind;

/** Shoppers of one store: profile, address book, account emails, merges and erasure. */
export const customersApi = {
  list: (
    storeId: string,
    params: CustomerListParams,
  ): Promise<GetAdminV1StoresStoreIdCustomers200> =>
    getAdminV1StoresStoreIdCustomers({ storeId }, params),
  get: (storeId: string, id: string): Promise<Customer> =>
    getAdminV1StoresStoreIdCustomersId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdCustomersBody): Promise<Customer> =>
    postAdminV1StoresStoreIdCustomers({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdCustomersIdBody,
  ): Promise<Customer> => patchAdminV1StoresStoreIdCustomersId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdCustomersId({ storeId, id }),
  merge: (storeId: string, targetId: string, sourceCustomerId: string): Promise<Customer> =>
    postAdminV1StoresStoreIdCustomersIdMerge({ storeId, id: targetId }, { sourceCustomerId }),
  timeline: (
    storeId: string,
    id: string,
    params: GetAdminV1StoresStoreIdCustomersIdTimelineParams,
  ): Promise<GetAdminV1StoresStoreIdCustomersIdTimeline200> =>
    getAdminV1StoresStoreIdCustomersIdTimeline({ storeId, id }, params),
  sendAccountEmail: (
    storeId: string,
    id: string,
    kind: AccountEmailKind,
    body: PostAdminV1StoresStoreIdCustomersIdInviteBody,
  ): Promise<CustomerEmailSent> =>
    kind === 'invite'
      ? postAdminV1StoresStoreIdCustomersIdInvite({ storeId, id }, body)
      : postAdminV1StoresStoreIdCustomersIdPasswordResetEmail({ storeId, id }, body),
  openPrivacyRequest: (
    storeId: string,
    id: string,
    kind: CustomerPrivacyKind,
  ): Promise<PrivacyRequest> =>
    postAdminV1StoresStoreIdCustomersIdPrivacyRequests({ storeId, id }, { kind }),
  addresses: (storeId: string, id: string): Promise<CustomerAddress[]> =>
    getAdminV1StoresStoreIdCustomersIdAddresses({ storeId, id }).then((r) => r.items),
  createAddress: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdCustomersIdAddressesBody,
  ): Promise<CustomerAddress> =>
    postAdminV1StoresStoreIdCustomersIdAddresses({ storeId, id }, body),
  updateAddress: (
    storeId: string,
    id: string,
    addressId: string,
    body: PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody,
  ): Promise<CustomerAddress> =>
    patchAdminV1StoresStoreIdCustomersIdAddressesAddressId({ storeId, id, addressId }, body),
  deleteAddress: (storeId: string, id: string, addressId: string): Promise<void> =>
    deleteAdminV1StoresStoreIdCustomersIdAddressesAddressId({ storeId, id, addressId }),
};
